import { useState, useEffect, useCallback } from 'react';
import { api, User, AccountOption } from '../lib/api';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingAccounts, setPendingAccounts] = useState<AccountOption[] | null>(null);
  const [tempToken, setTempToken] = useState<string | null>(null);

  const checkAuth = useCallback(async () => {
    const token = api.getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const user = await api.getCurrentUser();
      setUser(user);
    } catch (err) {
      api.setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const response = await api.login(email, password);
      if (response.requires_account_selection && response.accounts) {
        setPendingAccounts(response.accounts);
        setTempToken(response.temp_token || null);
        // Don't set user yet -- wait for account selection
      } else if (response.token && response.user) {
        setUser(response.user);
      }
      return response;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const selectAccount = async (customerId: string) => {
    if (!tempToken) return;
    setError(null);
    setLoading(true);
    try {
      const response = await api.selectAccount(customerId, tempToken);
      setUser(response.user);
      setPendingAccounts(null);
      setTempToken(null);
    } catch (err: any) {
      setError(err.message || 'Account selection failed');
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email: string, password: string, companyName: string, fullName: string) => {
    setError(null);
    try {
      const response = await api.signup(email, password, companyName, fullName);
      setUser(response.user);
      return response;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      setPendingAccounts(null);
      setTempToken(null);
    }
  };

  return {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    pendingAccounts,
    selectAccount,
    isAuthenticated: !!user,
  };
}
